import { DEVICE } from '@/shared/config';
import {
  checkToFormatPhoneNumber,
  formatPhoneNumber,
  handleFetchDivisions,
  checkNull,
} from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { PATIENT_DETAIL_KEY } from '../../config/patientDetailsTab';
import { initialContact } from '../../lib/patientDetailsTab';
import type { ContactDrawerProps } from './ContactDrawer.types';

/**
 * Holds the editable patient contact form of the study's patient details drawer. Seeds merged state
 * from the study's contact data, refetches state/province divisions whenever the country changes,
 * remembers the initial primary phone, and wires field, select and address-autocomplete handlers,
 * validation, the confirmation step and the submit that hands the contact payload to handleClick.
 *
 * @param props - Study data, drawer title and the parent save callback handleClick.
 * @returns An object carrying the form state and setter, whether e-mail is required, and the change,
 * autocomplete, save and submit handlers.
 */
export const useContactDrawer = ({
  data,
  title,
  handleClick,
}: Pick<ContactDrawerProps, 'data' | 'title' | 'handleClick'>) => {
  const initPhoneRef = useRef<any>(undefined);
  const [state, setState] = useMergeState<any>({
    ...initialContact(data),
    divisions: null,
    errorObject: {},
    isOpenConfirmModal: false,
  });

  /**
   * Whether an email address is mandatory for this study's patient contact. True for Bioflux Direct studies, and for BioTres devices whenever the selected country is not the US.
   *
   * @returns True when email must be provided.
   */
  const isEmailRequired =
    data?.isBiofluxDirect ||
    (_.includes(data?.device?.model, DEVICE.BIOTRES) && state.country !== 'US');

  /**
   * Loads the state or province list for the country selected in the patient contact drawer. Fetches divisions only for US and CA and stores them as divisions.
   *
   * @returns Resolves once the divisions have been stored, or immediately for other countries.
   */
  const getDivisions = async () => {
    if (['CA', 'US'].includes(state.country)) {
      const divisions = await handleFetchDivisions(state.country);
      setState({ divisions });
    }
  };

  /**
   * Writes one patient contact field and drops its validation error. Merges the value under its field name and clears that field's entry in errorObject.
   *
   * @param name - Contact field being edited.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const handleChange = (name: any, value: any) => {
    setState({
      [name]: value,
      errorObject: {
        ...state.errorObject,
        [name]: '',
      },
    });
  };

  /**
   * Applies a select change in the patient contact drawer. Stores the value under its field and, when the country changed, additionally blanks address, city, state, zip and the phone fields so they are re-entered for the new country.
   *
   * @param name - Contact field the select belongs to.
   * @param value - Selected value.
   * @returns Resolves once the selection has been committed to state.
   */
  const onChangeSelection = async (name: any, value: any) => {
    const setStateObject = { [name]: value };
    if (name === 'country') {
      _.assign(setStateObject, {
        address: '',
        city: '',
        state: '',
        zip: '',
        phone1: '',
        phone2: '',
        secondaryPhone: '',
      });
    }
    setState(setStateObject);
  };

  /**
   * Reflects typing in the patient contact drawer's address autocomplete input. Puts the text into city for a city search and into address otherwise, merged over a copy of the current state.
   *
   * @param value - Text currently typed in the autocomplete.
   * @param isSearchCity - True when the input searches cities rather than addresses.
   * @returns Nothing.
   */
  const onChangeInputAutoComplete = (value: any, isSearchCity: any) => {
    const setStateObject = { ...state };
    if (!isSearchCity) {
      setStateObject.address = value;
    } else {
      setStateObject.city = value;
    }
    setState(setStateObject);
  };

  /**
   * Fills the patient contact drawer's address fields from a chosen autocomplete suggestion. Copies city, state code and zip plus the street address unless it was a city search, and when the suggested country differs it switches country, fetches that country's divisions under isLoadingDivisions and keeps the state code only for US and CA, clearing it otherwise.
   *
   * @param data - Autocomplete suggestion carrying country, address, city, state and zip.
   * @param isSearchCity - True when the suggestion came from a city search.
   * @returns Resolves once the suggestion, and any newly fetched divisions, have been committed to state.
   */
  const onChangeAutoComplete = async (data: any, isSearchCity: any) => {
    const {
      country: autoCompleteCountry,
      address: autoCompleteAddress,
      city: autoCompleteCity,
      state: autoCompleteState,
      zip: autoCompleteZip,
    } = data;

    const setStateObject: any = {};

    if (!isSearchCity) {
      setStateObject.address = autoCompleteAddress;
    }
    setStateObject.city = autoCompleteCity;
    setStateObject.state = autoCompleteState.code;
    setStateObject.zip = autoCompleteZip;

    if (autoCompleteCountry) {
      const autoCompleteCountryCode = autoCompleteCountry?.code;

      if (autoCompleteCountryCode) {
        if (autoCompleteCountryCode !== setStateObject.country) {
          setStateObject.country = autoCompleteCountryCode;
          setState({ isLoadingDivisions: true });
          const divisions = await handleFetchDivisions(autoCompleteCountryCode);
          _.assign(setStateObject, { divisions, isLoadingDivisions: false });
        }

        if (autoCompleteCountryCode === 'US' || autoCompleteCountryCode === 'CA') {
          _.assign(setStateObject, {
            state: autoCompleteState.code,
          });
        } else {
          _.assign(setStateObject, { state: undefined });
        }
      }
    }
    setState(setStateObject);
  };

  /**
   * Clears the address fields of the patient contact drawer when an address autocomplete is emptied. Blanks city and state always and additionally the street address when the search was not a city search.
   *
   * @param isSearchCity - True when the cleared autocomplete searched cities rather than addresses.
   * @returns Nothing.
   */
  const clearAutoComplete = (isSearchCity: any) => {
    const setStateObject = { ...state };
    setStateObject.city = '';
    setStateObject.state = undefined;
    if (!isSearchCity) {
      setStateObject.address = '';
    }
    setState(setStateObject);
  };

  /**
   * Checks the patient contact drawer's fields before saving. Runs checkNull over country, address, city, state, zip and phone1, the conditionally required e-mail and the optional phone2, storing any findings in errorObject and returning false, or true when nothing was missing.
   *
   * @returns True when every required contact field is filled.
   */
  const validateData = () => {
    const checkNullData = [];
    const errorObject = {};

    checkNullData.push(
      { field: 'country', value: state.country, isRequired: true },
      { field: 'address', value: state.address, isRequired: true },
      { field: 'city', value: state.city, isRequired: true },
      { field: 'state', value: state.state, isRequired: true },
      { field: 'zip', value: state.zip, isRequired: true },
      { field: 'phone1', value: state.phone1, isRequired: true },
      { field: 'email', value: state.email, isRequired: isEmailRequired },
      { field: 'phone2', value: state.phone2, isRequired: false },
    );
    const checkNullError = checkNull(checkNullData);
    if (!_.isEmpty(checkNullError)) {
      _.assign(errorObject, { ...checkNullError });
    }

    if (!_.isEmpty(errorObject)) {
      setState({ errorObject });
      return false;
    }
    return true;
  };

  /**
   * Starts saving the patient contact drawer. Validates the fields first and opens the confirmation modal only when validation passed.
   *
   * @returns Nothing.
   */
  const onClickSave = () => {
    const isValidData = validateData();
    if (!isValidData) {
      return;
    }
    setState({ isOpenConfirmModal: true });
  };

  /**
   * Submits the patient contact drawer's address, phone and e-mail edits. Formats phone1 and phone2 when the country calls for it, trims the fields into a patient.contact payload, and calls handleClick under the patient-contact key with couldCheckReportSize set when the primary phone differs from its initial value.
   *
   * @returns Nothing.
   */
  const onClickBottomButton = () => {
    const shouldFormatPhoneNumber = checkToFormatPhoneNumber(state.country);
    const phone1 = state.phone1
      ? shouldFormatPhoneNumber
        ? formatPhoneNumber(state.phone1)
        : state.phone1
      : undefined;
    const phone2 = state?.phone2
      ? shouldFormatPhoneNumber
        ? formatPhoneNumber(state.phone2)
        : state.phone2
      : undefined;

    const payload = {
      patient: {
        contact: {
          country: state?.country,
          address: state?.address?.trim(),
          city: state?.city?.trim(),
          state: state?.state?.trim(),
          zip: state?.zip?.trim(),
          phone1,
          phone2,
        },
        email: state?.email?.trim(),
      },
    };
    const couldCheckReportSize = initPhoneRef.current !== phone1;
    handleClick({
      payload,
      status: true,
      key: PATIENT_DETAIL_KEY.PATIENT_CONTACT,
      couldCheckReportSize,
    });
  };

  useEffect(() => {
    getDivisions();
  }, [state.country]);

  useEffect(() => {
    if (title && !_.isEmpty(data)) {
      const { phone1 } = initialContact(data);
      initPhoneRef.current = phone1;
    }
  }, [title, data]);

  return {
    state,
    setState,
    isEmailRequired,
    handleChange,
    onChangeSelection,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
    clearAutoComplete,
    onClickSave,
    onClickBottomButton,
  };
};
